// moderationRuleRegistry — central registry av moderation-regler.
//
// Varje regel har:
//   - rule_id: stabil identifier (dot-notation: "safety.exfiltration")
//   - version: bump:as när pattern/regel förändras så vi kan spåra hur
//     klassifikationen såg ut historiskt
//   - severity: 'advisory' (warna, räkna inte) | 'violation' (räkna strike)
//   - humanReadable: svensk/engelsk text som visas för user
//   - matcher(prompt) → matchedPattern | null
//
// Meta-query exemption: regeln 'meta.moderation_query' fångar frågor om
// flaggningar själva ("varför blev jag flaggad?") och matchar INNAN
// content-regler körs. Den har severity='advisory' och bara markerar
// prompten som meta så pipelinen vet att skippa content-klassificering.

export type Severity = 'advisory' | 'violation';

export interface ModerationRule {
  ruleId: string;
  version: number;
  severity: Severity;
  /** Visas i UI som förklaring. Default-språk är svenska. */
  humanReadable: string;
  humanReadableEn?: string;
  /** Om matcher returnerar en sträng har regeln matchat. Null = ingen träff. */
  matcher: (prompt: string) => string | null;
  /** Om true, behandlas prompten som meta-fråga och content-regler skippas. */
  isMetaBypass?: boolean;
}

/**
 * Enkel regex-matcher som returnerar den första matchade texten (för
 * debugging i matched_pattern-fältet).
 */
function re(pattern: RegExp): (prompt: string) => string | null {
  return (prompt: string) => {
    const m = prompt.match(pattern);
    return m ? m[0].slice(0, 200) : null;
  };
}

export const MODERATION_RULES: readonly ModerationRule[] = [
  // ── Meta-regel: MÅSTE köras först ──────────────────────────────────
  {
    ruleId: 'meta.moderation_query',
    version: 1,
    severity: 'advisory',
    humanReadable:
      'Detta ser ut som en meta-fråga om moderationssystemet. Meta-frågor ' +
      'räknas aldrig som överträdelser.',
    humanReadableEn:
      'This looks like a meta-question about the moderation system. ' +
      'Meta-questions never count as violations.',
    isMetaBypass: true,
    matcher: re(
      /\b(varför|hur kommer det sig|why|how come|how did)\b[^.!?]*\b(flagg|varn|block|strike|ban|moderat|polic[yi]|regel|rule)/i,
    ),
  },
  {
    ruleId: 'meta.history_query',
    version: 1,
    severity: 'advisory',
    humanReadable:
      'Detta är en fråga om din egen historik i systemet — ingen moderation behövs.',
    isMetaBypass: true,
    matcher: re(
      /\b(mina|min|my)\b[^.!?]*\b(chattar|historik|history|conversations|flöden)\b/i,
    ),
  },

  // ── Violation-regler (räknas mot strikes) ──────────────────────────
  {
    ruleId: 'safety.exfiltration',
    version: 1,
    severity: 'violation',
    humanReadable:
      'Din prompt försöker få ut den interna systemprompten, verktygsdefinitioner ' +
      'eller hemliga instruktioner. Det är inte tillåtet.',
    humanReadableEn:
      'Your prompt attempts to exfiltrate the internal system prompt, tool ' +
      'definitions, or hidden instructions. Not allowed.',
    matcher: re(
      /(system\s*prompts?|tool\s*(definitions?|enum|list|names?)|your\s*instructions|internal\s*(prompt|rules|spec))/i,
    ),
  },
  // OBS: jailbreak-persona MÅSTE komma före prompt_injection eftersom
  // "you are now DAN" matchar båda — vi vill att jailbreak-regeln tar
  // prioritet (mer specifik rule_id → bättre UX-förklaring).
  {
    ruleId: 'safety.jailbreak_persona',
    version: 1,
    severity: 'violation',
    humanReadable:
      'Din prompt använder en känd jailbreak-persona (DAN, STAN, osv) eller ' +
      '"låtsas-vara"-mönster för att kringgå säkerhetsrestriktioner.',
    matcher: re(
      /\b(DAN|STAN|DUDE)\b|\b(do anything now|pretend to be|roleplay as)\s+(no restrictions?|uncensored|evil)|you\s+are\s+now\s+(DAN|STAN|DUDE)/i,
    ),
  },
  {
    ruleId: 'safety.prompt_injection',
    version: 1,
    severity: 'violation',
    humanReadable:
      'Din prompt försöker åsidosätta tidigare instruktioner eller byta rollen ' +
      'på assistenten. Det är en känd attack-vektor.',
    humanReadableEn:
      'Your prompt tries to override prior instructions or switch the assistant\'s role.',
    matcher: re(
      /\b(ignore\s+(all\s+)?(previous|prior|above)\s+(instructions?|rules?)|forget\s+(everything|all)|you\s+are\s+now\b|new\s+instructions?:)/i,
    ),
  },

  // ── Advisory-regler (loggas, räknas INTE) ──────────────────────────
  {
    ruleId: 'advisory.off_topic',
    version: 1,
    severity: 'advisory',
    humanReadable:
      'Din fråga verkar ligga utanför vad AI-assistenten är designad för ' +
      '(workflow-design). Du får ingen strike — bara en notis.',
    matcher: re(/\b(recept|movie recommendation|stock tips|weather)\b/i),
  },
] as const;

/**
 * Klassificera en prompt mot registry.
 * Returnerar första träffen (meta-regler går först → content-regler).
 * Om meta-match → skipContentCheck=true även om severity är advisory.
 */
export interface ClassifyResult {
  matched: boolean;
  rule?: ModerationRule;
  matchedPattern?: string;
  skipContentCheck?: boolean;
}

export function classifyAgainstRegistry(prompt: string): ClassifyResult {
  for (const rule of MODERATION_RULES) {
    const hit = rule.matcher(prompt);
    if (hit !== null) {
      return {
        matched: true,
        rule,
        matchedPattern: hit,
        skipContentCheck: rule.isMetaBypass === true,
      };
    }
  }
  return { matched: false };
}

/** Hitta en regel på ruleId — används för UI display + testing. */
export function getRule(ruleId: string): ModerationRule | undefined {
  return MODERATION_RULES.find((r) => r.ruleId === ruleId);
}
